import { BarChart, MONTHS, Metric, PageHead } from "@getaways/extranet-ui";
import { useCan } from "../lib/access";
import { FilterBar, lastYear, presetRange, useFilters, useOptions } from "../lib/filters";
import { eur, int } from "../lib/format";
import { compactEur, filterOf, ResultTable, useQuery } from "../lib/query";

const delta = (now: number | null | undefined, before: number | null | undefined) => {
  if (now === null || now === undefined || !before) return undefined;
  const d = ((now - before) / Math.abs(before)) * 100;
  return <span style={{ color: d >= 0 ? "var(--ok)" : "var(--bad)" }}>{d >= 0 ? "▲" : "▼"} {Math.abs(d).toFixed(1)}% vs last year</span>;
};

/** The company at a glance (docs/36 §7): sold, margin, orders, by brand, product and market, against last year. */
export function OverviewPage() {
  const money = useCan()("money:read");
  const [filters, setFilters] = useFilters("overview");
  const options = useOptions();
  const measures = ["orders", "pax", "sell", "avg_order", ...(money ? ["margin", "margin_pct"] : [])];
  const now = useQuery({ table: "orders", measures, filter: filterOf(filters) });
  const before = useQuery({ table: "orders", measures, filter: filterOf(filters, lastYear(filters.range)) });
  const t = now.data?.totals, b = before.data?.totals;

  // Twelve months to the end of the period, this year and the one before, by booking month.
  const end = filters.range.to;
  const twelve = { from: presetRange("last_12", end).from, to: end };
  const trend = useQuery({ table: "orders", measures: ["sell"], dimensions: ["booked_month"], filter: filterOf({ ...filters, basis: "booked" }, twelve), sort: "booked_month", dir: "asc", limit: 24 });
  const trendBefore = useQuery({ table: "orders", measures: ["sell"], dimensions: ["booked_month"], filter: filterOf({ ...filters, basis: "booked" }, lastYear(twelve)), sort: "booked_month", dir: "asc", limit: 24 });
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(Number(end.slice(0, 4)), Number(end.slice(5, 7)) - 12 + i, 1));
    return d.toISOString().slice(0, 7);
  });
  const valueOf = (rows: Record<string, string | number | null>[] | undefined, month: string) => (rows ? Number(rows.find((r) => r.booked_month === month)?.sell ?? 0) : null);
  const minusYear = (m: string) => `${Number(m.slice(0, 4)) - 1}${m.slice(4)}`;

  const f = filterOf(filters);
  const money3 = money ? ["margin", "margin_pct"] : [];
  return (
    <>
      <PageHead title="Overview" sub="Sales across the three brands and every product, against the same days last year." />
      <div className="card" style={{ padding: 0, marginBottom: 14 }}>
        <FilterBar filters={filters} onChange={setFilters} options={options} supplierFilter={false} />
      </div>
      <div className="metrics">
        <Metric label="Sold (EUR)" value={eur(t?.sell)} delta={delta(t?.sell, b?.sell)} />
        {money && <Metric label="Margin" value={eur(t?.margin)} hint={t ? `${t.margin_pct ?? "—"}% of sales` : undefined} delta={delta(t?.margin, b?.margin)} />}
        <Metric label="Orders" value={int(t?.orders)} delta={delta(t?.orders, b?.orders)} />
        <Metric label="Travellers" value={int(t?.pax)} delta={delta(t?.pax, b?.pax)} />
        <Metric label="Average order" value={eur(t?.avg_order)} delta={delta(t?.avg_order, b?.avg_order)} />
      </div>
      <section className="card" style={{ marginBottom: 14 }}>
        <h2 style={{ marginTop: 0, fontSize: 15 }}>Sold by booking month</h2>
        <BarChart
          label="Sold by booking month, the last twelve months and the year before"
          categories={months.map((m) => MONTHS[Number(m.slice(5, 7)) - 1]!)}
          categoryTitle={(i) => months[i]!}
          format={(v) => eur(v)}
          axisFormat={compactEur}
          series={[
            { label: "Year before", color: "var(--series-1)", values: months.map((m) => valueOf(trendBefore.data?.rows, minusYear(m))) },
            { label: "Last 12 months", color: "var(--series-2)", values: months.map((m) => valueOf(trend.data?.rows, m)) },
          ]}
        />
      </section>
      <div className="grid-2">
        <ResultTable title="By brand" spec={{ table: "orders", measures: ["orders", "sell", ...money3], dimensions: ["brand"], filter: f }} />
        <ResultTable title="By product" spec={{ table: "items", measures: ["item_orders", "item_sell", ...(money ? ["item_margin", "item_margin_pct"] : [])], dimensions: ["vertical"], filter: f }} />
        <ResultTable title="By market" spec={{ table: "orders", measures: ["orders", "sell", ...money3], dimensions: ["market"], filter: f }} />
        <ResultTable title="Top destinations" spec={{ table: "items", measures: ["item_orders", "room_nights", "item_sell"], dimensions: ["destination"], filter: { ...f, verticals: ["hotel"] }, limit: 15 }} />
      </div>
      <p className="small muted">Sales are confirmed orders; money in EUR at each order's locked rates. Margin is {money ? "sold minus discounts, supplier cost and partner commission" : "not shown for your role"}. Legacy bookings count from their legacy booking date.</p>
    </>
  );
}
